import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  BackupsWorkload,
  BackupsWorkloadDetails,
  BackupsWorkloadSnapshot,
  BackupsWorkloadSnapshotDetails,
  CancelBackupsWorkloadSnapshotsParams,
  CancelBackupsWorkloadSnapshotsResponse,
  CancelRestoreParams,
  CancelRestoreResponse,
  CreateBackupsWorkloadParams,
  CreateBackupsWorkloadResponse,
  CreateBackupsWorkloadSnapshotsParams,
  CreateBackupsWorkloadSnapshotsResponse,
  CreateInPlaceRestoreParams,
  CreateInPlaceRestoreResponse,
  CreateSelectiveRestoreParams,
  CreateSelectiveRestoreResponse,
  DeleteBackupsWorkloadParams,
  DeleteBackupsWorkloadResponse,
  DeleteBackupsWorkloadSnapshotsParams,
  DeleteBackupsWorkloadSnapshotsResponse,
  DeleteRestoreParams,
  DeleteRestoreResponse,
  EditBackupsWorkloadParams,
  EditBackupsWorkloadResponse,
  GetBackupsWorkloadDetailsParams,
  GetBackupsWorkloadDetailsResponse,
  GetBackupsWorkloadSnapshotDetailsParams,
  GetBackupsWorkloadSnapshotDetailsResponse,
  GetBackupsWorkloadSnapshotsParams,
  GetBackupsWorkloadSnapshotsResponse,
  GetBackupsWorkloadsParams,
  GetBackupsWorkloadsResponse,
  GetRestoresParams,
  GetRestoresResponse,
  MultiDeleteBackupsWorkloadSnapshotsParams,
  MultiDeleteBackupsWorkloadSnapshotsResponse,
  RestoreFromList
} from "./types";

export type BackupsState = {
  backupsWorkloads: BackupsWorkload[] | null;
  areBackupsWorkloadsLoading: boolean;
  backupsWorkloadDetails: BackupsWorkloadDetails | null;
  isBackupsWorkloadDetailsLoading: boolean;
  isBackupsWorkloadCreating: boolean;
  isBackupsWorkloadEditing: boolean;
  isBackupsWorkloadDeleting: boolean;
  backupsSnapshots: BackupsWorkloadSnapshot[] | null;
  areBackupsSnapshotsLoading: boolean;
  isBackupsWorkloadSnapshotDeleting: boolean;
  areBackupsWorkloadSnapshotsMultiDeleting: boolean;
  isBackupsWorkloadSnapshotCreating: boolean;
  isBackupsWorkloadSnapshotCanceling: boolean;
  backupsSnapshotDetails: BackupsWorkloadSnapshotDetails | null;
  isBackupsSnapshotDetailsLoading: boolean;
  restores: RestoreFromList[] | null;
  areRestoresLoading: boolean;
  isSelectiveRestoreCreating: boolean;
  isInPlaceRestoreCreating: boolean;
  isRestoreDeleting: boolean;
  isRestoreCanceling: boolean;
};

const initialState: BackupsState = {
  backupsWorkloads: null,
  areBackupsWorkloadsLoading: false,
  backupsWorkloadDetails: null,
  isBackupsWorkloadDetailsLoading: false,
  isBackupsWorkloadCreating: false,
  isBackupsWorkloadEditing: false,
  isBackupsWorkloadDeleting: false,
  backupsSnapshots: null,
  areBackupsSnapshotsLoading: false,
  isBackupsWorkloadSnapshotCreating: false,
  isBackupsWorkloadSnapshotDeleting: false,
  areBackupsWorkloadSnapshotsMultiDeleting: false,
  isBackupsWorkloadSnapshotCanceling: false,
  backupsSnapshotDetails: null,
  isBackupsSnapshotDetailsLoading: false,
  restores: null,
  areRestoresLoading: false,
  isSelectiveRestoreCreating: false,
  isInPlaceRestoreCreating: false,
  isRestoreDeleting: false,
  isRestoreCanceling: false
};

const backupsSlice = createSlice({
  name: "backups",
  initialState,
  reducers: {
    // Get Backups Workloads
    getBackupsWorkloadsRequest: (
      state,
      action: PayloadAction<GetBackupsWorkloadsParams>
    ) => {
      state.areBackupsWorkloadsLoading = true;
    },
    getBackupsWorkloadsSuccess: (
      state,
      action: PayloadAction<GetBackupsWorkloadsResponse>
    ) => {
      state.backupsWorkloads = action.payload;
      state.areBackupsWorkloadsLoading = false;
    },
    getBackupsWorkloadsFailure: (
      state,
      action: PayloadAction<{
        params: GetBackupsWorkloadsParams;
        error: string;
      }>
    ) => {
      state.areBackupsWorkloadsLoading = false;
    },

    // Get Backups Workload Details
    getBackupsWorkloadDetailsRequest: (
      state,
      action: PayloadAction<GetBackupsWorkloadDetailsParams>
    ) => {
      state.isBackupsWorkloadDetailsLoading = true;
    },
    getBackupsWorkloadDetailsSuccess: (
      state,
      action: PayloadAction<GetBackupsWorkloadDetailsResponse>
    ) => {
      state.backupsWorkloadDetails = action.payload.workload;
      state.isBackupsWorkloadDetailsLoading = false;
    },
    getBackupsWorkloadDetailsFailure: (
      state,
      action: PayloadAction<{
        params: GetBackupsWorkloadDetailsParams;
        error: string;
      }>
    ) => {
      state.isBackupsWorkloadDetailsLoading = false;
    },

    // Create Backups Workload
    createBackupsWorkloadRequest: (
      state,
      action: PayloadAction<CreateBackupsWorkloadParams>
    ) => {
      state.isBackupsWorkloadCreating = true;
    },
    createBackupsWorkloadSuccess: (
      state,
      action: PayloadAction<CreateBackupsWorkloadResponse>
    ) => {
      state.isBackupsWorkloadCreating = false;
    },
    createBackupsWorkloadFailure: (
      state,
      action: PayloadAction<{
        params: CreateBackupsWorkloadParams;
        error: string;
      }>
    ) => {
      state.isBackupsWorkloadCreating = false;
    },

    // Edit Backups Workload
    editBackupsWorkloadRequest: (
      state,
      action: PayloadAction<EditBackupsWorkloadParams>
    ) => {
      state.isBackupsWorkloadEditing = true;
    },
    editBackupsWorkloadSuccess: (
      state,
      action: PayloadAction<EditBackupsWorkloadResponse>
    ) => {
      state.isBackupsWorkloadEditing = false;
    },
    editBackupsWorkloadFailure: (
      state,
      action: PayloadAction<{
        params: EditBackupsWorkloadParams;
        error: string;
      }>
    ) => {
      state.isBackupsWorkloadEditing = false;
    },

    // Delete Backups Workload
    deleteBackupsWorkloadRequest: (
      state,
      action: PayloadAction<DeleteBackupsWorkloadParams>
    ) => {
      state.isBackupsWorkloadDeleting = true;
    },
    deleteBackupsWorkloadSuccess: (
      state,
      action: PayloadAction<DeleteBackupsWorkloadResponse>
    ) => {
      state.isBackupsWorkloadDeleting = false;
      state.backupsWorkloadDetails = null;
    },
    deleteBackupsWorkloadFailure: (
      state,
      action: PayloadAction<{
        params: DeleteBackupsWorkloadParams;
        error: string;
      }>
    ) => {
      state.isBackupsWorkloadDeleting = false;
    },

    // Get Backups Workload Snapshots
    getBackupsWorkloadSnapshotsRequest: (
      state,
      action: PayloadAction<GetBackupsWorkloadSnapshotsParams>
    ) => {
      state.areBackupsSnapshotsLoading = true;
    },
    getBackupsWorkloadSnapshotsSuccess: (
      state,
      action: PayloadAction<GetBackupsWorkloadSnapshotsResponse>
    ) => {
      state.backupsSnapshots = action.payload;
      state.areBackupsSnapshotsLoading = false;
    },
    getBackupsWorkloadSnapshotsFailure: (
      state,
      action: PayloadAction<{
        params: GetBackupsWorkloadSnapshotsParams;
        error: string;
      }>
    ) => {
      state.areBackupsSnapshotsLoading = false;
    },

    // Create Backups Workload Snapshot
    createBackupsWorkloadSnapshotRequest: (
      state,
      action: PayloadAction<CreateBackupsWorkloadSnapshotsParams>
    ) => {
      state.isBackupsWorkloadSnapshotCreating = true;
    },
    createBackupsWorkloadSnapshotSuccess: (
      state,
      action: PayloadAction<CreateBackupsWorkloadSnapshotsResponse>
    ) => {
      state.isBackupsWorkloadSnapshotCreating = false;
    },
    createBackupsWorkloadSnapshotFailure: (
      state,
      action: PayloadAction<{
        params: CreateBackupsWorkloadSnapshotsParams;
        error: string;
      }>
    ) => {
      state.isBackupsWorkloadSnapshotCreating = false;
    },

    // Delete Backups Workload Snapshot
    deleteBackupsWorkloadSnapshotRequest: (
      state,
      action: PayloadAction<DeleteBackupsWorkloadSnapshotsParams>
    ) => {
      state.isBackupsWorkloadSnapshotDeleting = true;
    },
    deleteBackupsWorkloadSnapshotSuccess: (
      state,
      action: PayloadAction<DeleteBackupsWorkloadSnapshotsResponse>
    ) => {
      state.backupsSnapshotDetails = null;
      state.isBackupsWorkloadSnapshotDeleting = false;
    },
    deleteBackupsWorkloadSnapshotFailure: (
      state,
      action: PayloadAction<{
        params: DeleteBackupsWorkloadSnapshotsParams;
        error: string;
      }>
    ) => {
      state.isBackupsWorkloadSnapshotDeleting = false;
    },

    // Multi-delete Backups Workload Snapshots
    multiDeleteBackupsWorkloadSnapshotsRequest: (
      state,
      action: PayloadAction<MultiDeleteBackupsWorkloadSnapshotsParams>
    ) => {
      state.areBackupsWorkloadSnapshotsMultiDeleting = true;
    },
    multiDeleteBackupsWorkloadSnapshotsSuccess: (
      state,
      action: PayloadAction<MultiDeleteBackupsWorkloadSnapshotsResponse>
    ) => {
      state.areBackupsWorkloadSnapshotsMultiDeleting = false;
    },
    multiDeleteBackupsWorkloadSnapshotsFailure: (
      state,
      action: PayloadAction<{
        params: MultiDeleteBackupsWorkloadSnapshotsParams;
        error: string;
      }>
    ) => {
      state.areBackupsWorkloadSnapshotsMultiDeleting = false;
    },

    // Cancel Backups Workload Snapshot
    cancelBackupsWorkloadSnapshotRequest: (
      state,
      action: PayloadAction<CancelBackupsWorkloadSnapshotsParams>
    ) => {
      state.isBackupsWorkloadSnapshotCanceling = true;
    },
    cancelBackupsWorkloadSnapshotSuccess: (
      state,
      action: PayloadAction<CancelBackupsWorkloadSnapshotsResponse>
    ) => {
      state.isBackupsWorkloadSnapshotCanceling = false;
    },
    cancelBackupsWorkloadSnapshotFailure: (
      state,
      action: PayloadAction<{
        params: CancelBackupsWorkloadSnapshotsParams;
        error: string;
      }>
    ) => {
      state.isBackupsWorkloadSnapshotCanceling = false;
    },

    // Get Backups Workload Snapshot Details
    getBackupsWorkloadSnapshotDetailsRequest: (
      state,
      action: PayloadAction<GetBackupsWorkloadSnapshotDetailsParams>
    ) => {
      state.isBackupsSnapshotDetailsLoading = true;
    },
    getBackupsWorkloadSnapshotDetailsSuccess: (
      state,
      action: PayloadAction<GetBackupsWorkloadSnapshotDetailsResponse>
    ) => {
      state.backupsSnapshotDetails = action.payload.snapshot;
      state.isBackupsSnapshotDetailsLoading = false;
    },
    getBackupsWorkloadSnapshotDetailsFailure: (
      state,
      action: PayloadAction<{
        params: GetBackupsWorkloadSnapshotDetailsParams;
        error: string;
      }>
    ) => {
      state.isBackupsSnapshotDetailsLoading = false;
    },

    // Get Restores
    getRestoresRequest: (state, action: PayloadAction<GetRestoresParams>) => {
      state.areRestoresLoading = true;
    },
    getRestoresSuccess: (state, action: PayloadAction<GetRestoresResponse>) => {
      state.restores = action.payload;
      state.areRestoresLoading = false;
    },
    getRestoresFailure: (
      state,
      action: PayloadAction<{ params: GetRestoresParams; error: string }>
    ) => {
      state.areRestoresLoading = false;
    },

    // Create Selective Restore
    createSelectiveRestoreRequest: (
      state,
      action: PayloadAction<CreateSelectiveRestoreParams>
    ) => {
      state.isSelectiveRestoreCreating = true;
    },
    createSelectiveRestoreSuccess: (
      state,
      action: PayloadAction<CreateSelectiveRestoreResponse>
    ) => {
      state.isSelectiveRestoreCreating = false;
    },
    createSelectiveRestoreFailure: (
      state,
      action: PayloadAction<{
        params: CreateSelectiveRestoreParams;
        error: string;
      }>
    ) => {
      state.isSelectiveRestoreCreating = false;
    },

    // Create InPlace Restore
    createInPlaceRestoreRequest: (
      state,
      action: PayloadAction<CreateInPlaceRestoreParams>
    ) => {
      state.isInPlaceRestoreCreating = true;
    },
    createInPlaceRestoreSuccess: (
      state,
      action: PayloadAction<CreateInPlaceRestoreResponse>
    ) => {
      state.isInPlaceRestoreCreating = false;
    },
    createInPlaceRestoreFailure: (
      state,
      action: PayloadAction<{
        params: CreateInPlaceRestoreParams;
        error: string;
      }>
    ) => {
      state.isInPlaceRestoreCreating = false;
    },

    // Delete Restore
    deleteRestoreRequest: (
      state,
      action: PayloadAction<DeleteRestoreParams>
    ) => {
      state.isRestoreDeleting = true;
    },
    deleteRestoreSuccess: (
      state,
      action: PayloadAction<DeleteRestoreResponse>
    ) => {
      state.isRestoreDeleting = false;
    },
    deleteRestoreFailure: (
      state,
      action: PayloadAction<{ params: DeleteRestoreParams; error: string }>
    ) => {
      state.isRestoreDeleting = false;
    },

    // Cancel Restore
    cancelRestoreRequest: (
      state,
      action: PayloadAction<CancelRestoreParams>
    ) => {
      state.isRestoreCanceling = true;
    },
    cancelRestoreSuccess: (
      state,
      action: PayloadAction<CancelRestoreResponse>
    ) => {
      state.isRestoreCanceling = false;
    },
    cancelRestoreFailure: (
      state,
      action: PayloadAction<{ params: CancelRestoreParams; error: string }>
    ) => {
      state.isRestoreCanceling = false;
    },

    // Clear
    clearBackupsWorkloads: (state) => {
      state.backupsWorkloads = initialState.backupsWorkloads;
    },
    clearWorkloadDetails: (state) => {
      state.backupsWorkloadDetails = initialState.backupsWorkloadDetails;
    },
    clearBackupsSnapshots: (state) => {
      state.backupsSnapshots = initialState.backupsSnapshots;
    },
    clear: () => initialState
  }
});

export const {
  getBackupsWorkloadsRequest,
  getBackupsWorkloadsSuccess,
  getBackupsWorkloadsFailure,
  getBackupsWorkloadDetailsRequest,
  getBackupsWorkloadDetailsSuccess,
  getBackupsWorkloadDetailsFailure,
  createBackupsWorkloadRequest,
  createBackupsWorkloadSuccess,
  createBackupsWorkloadFailure,
  editBackupsWorkloadRequest,
  editBackupsWorkloadSuccess,
  editBackupsWorkloadFailure,
  deleteBackupsWorkloadRequest,
  deleteBackupsWorkloadSuccess,
  deleteBackupsWorkloadFailure,
  getBackupsWorkloadSnapshotsRequest,
  getBackupsWorkloadSnapshotsSuccess,
  getBackupsWorkloadSnapshotsFailure,
  createBackupsWorkloadSnapshotRequest,
  createBackupsWorkloadSnapshotSuccess,
  createBackupsWorkloadSnapshotFailure,
  deleteBackupsWorkloadSnapshotRequest,
  deleteBackupsWorkloadSnapshotSuccess,
  deleteBackupsWorkloadSnapshotFailure,
  multiDeleteBackupsWorkloadSnapshotsRequest,
  multiDeleteBackupsWorkloadSnapshotsSuccess,
  multiDeleteBackupsWorkloadSnapshotsFailure,
  cancelBackupsWorkloadSnapshotRequest,
  cancelBackupsWorkloadSnapshotSuccess,
  cancelBackupsWorkloadSnapshotFailure,
  getBackupsWorkloadSnapshotDetailsRequest,
  getBackupsWorkloadSnapshotDetailsSuccess,
  getBackupsWorkloadSnapshotDetailsFailure,
  getRestoresRequest,
  getRestoresSuccess,
  getRestoresFailure,
  createSelectiveRestoreRequest,
  createSelectiveRestoreSuccess,
  createSelectiveRestoreFailure,
  createInPlaceRestoreRequest,
  createInPlaceRestoreSuccess,
  createInPlaceRestoreFailure,
  deleteRestoreRequest,
  deleteRestoreSuccess,
  deleteRestoreFailure,
  cancelRestoreRequest,
  cancelRestoreSuccess,
  cancelRestoreFailure,
  clearBackupsWorkloads,
  clearWorkloadDetails,
  clearBackupsSnapshots,
  clear
} = backupsSlice.actions;

export const { actions: backupsActions } = backupsSlice;

export const reducer = backupsSlice.reducer;
